import React, { useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { Option, Product, Category } from '../../models/Menu';
import { OptionListProps, OptionListState, DragEndEvent, DragStartEvent, SyntheticListenerMap, CardItemProps } from './OptionList.types';
import { returnCategories } from '../../utils/product';
import { checkAccess } from '../../utils/admins';
import { UNIT } from '../../constants';
import CardItem from '../CardItem';
import {
  handleOpenModal as openModal,
  showDeleteOptionModal,
  closeModal,
  handleDragOption,
  handleStartDrag,
  handleDuplicateOption,
  handleRemoveOption
} from './OptionList.services.hooks';

const INITIAL_STATE: OptionListState = {
  isLoading: false,
  createOption: false,
  isSorting: false,
  showOptionModal: false,
  activeId: null,
  showModal: false,
  option: {} as Option,
  synchronize: false,
  selectedShops: {}
};

const CardItemTyped = CardItem as React.ComponentType<CardItemProps>;

const useStableProps = (props: OptionListProps) => {
  return useMemo(() => ({
    categoryKey: props.categoryKey,
    onActionComplete: props.onActionComplete,
    sections: props.sections,
    options: props.options,
    categories: props.categories,
    classes: props.classes,
    allOptions: props.allOptions
  }), [
    props.categoryKey,
    props.onActionComplete,
    props.sections,
    props.options,
    props.categories,
    props.classes,
    props.allOptions
  ]);
};

export const useOptionList = (props: OptionListProps): {
  state: OptionListState;
  setState: React.Dispatch<React.SetStateAction<OptionListState>>;
  sortedOptions: Record<string, Option>;
  allProducts: Product[];
  handleOpenModal: (option: Option | Record<string, never>) => void;
  handleCloseModal: () => void;
  handleShowDeleteModal: (option: Option) => void;
  handleDuplicate: (option: Option) => Promise<void>;
  handleRemove: (option: Option) => Promise<void>;
  handleDrag: (event: DragEndEvent, options: Record<string, Option>) => Promise<void>;
  handleStart: (event: DragStartEvent) => void;
  renderOptionItem: (optionKey: string, handleProps: SyntheticListenerMap | undefined) => React.ReactElement | null;
} => {
  const [state, setState] = useState<OptionListState>(INITIAL_STATE);
  const stableProps = useStableProps(props);

  const stateRef = React.useRef(state);
  stateRef.current = state;

  const allProducts = useMemo(() => {
    if (!stableProps.sections) return [];
    return Object.values(stableProps.sections).flatMap(section =>
      section?.products ? Object.values(section.products) : []
    );
  }, [stableProps.sections]);

  const sortedOptions = useMemo(() => {
    const options = Object.fromEntries(
      Object.entries(stableProps.options || {}).sort(([, a], [, b]) => (a?.position || 0) - (b?.position || 0))
    );

    return options;
  }, [stableProps.options]);

  const handleOpenModal = useCallback((option: Option | Record<string, never>) => {
    openModal(option, setState);
  }, []);

  const handleCloseModal = useCallback(() => {
    closeModal(setState);
  }, []);

  const handleShowDeleteModal = useCallback((option: Option) => {
    showDeleteOptionModal(option, props, stateRef, setState);
  }, [props]);

  const handleDuplicate = useCallback(async (option: Option): Promise<void> => {
    await handleDuplicateOption(option, props, state, setState, stableProps.onActionComplete);
  }, [props, state, stableProps.onActionComplete]);

  const handleRemove = useCallback(async (option: Option): Promise<void> => {
    await handleRemoveOption(option, props, state, setState, stableProps.onActionComplete);
  }, [props, state, stableProps.onActionComplete]);

  const handleDrag = useCallback(async (event: any, options: Record<string, Option>): Promise<void> => {
    await handleDragOption(event, options, stableProps.categoryKey, setState, props.shopId);
  }, [stableProps.categoryKey, props.shopId]);

  const handleStart = useCallback((event: DragStartEvent) => {
    handleStartDrag(event, setState);
  }, []);

  const formatCategories = useCallback((categoriesArray: Category[]) => {
    if (categoriesArray.length === 0) return '--';
    return categoriesArray.map(category => ` ${category.name}`).join('');
  }, []);

  const renderOptionItem = useCallback((optionKey: string, handleProps: SyntheticListenerMap | undefined) => {
    const { classes, categories, options, allOptions } = stableProps;
    const { isSorting } = state;
    const option = options[optionKey];

    if (!option) {
      return null;
    }

    if (!option.key) {
      option.key = optionKey || `missing-key-${Date.now()}`;
    }

    const { productKey = '' } = option;
    const attachedproduct = allProducts.find(produit => produit.key === productKey);
    const categoriesArray = returnCategories(option?.subcategories, categories);
    const optionsForDeletionCheck = allOptions || options;
    const canDelete = _.size(optionsForDeletionCheck) >= 2;

    const uniqueKey = option.key || optionKey || `fallback-${Date.now()}-${Math.random()}`;

    return (
      <div {...handleProps}>
        <CardItemTyped
          key={uniqueKey}
          item={option}
          className={[classes.paper, classes.maxPaper]}
          onClick={() =>
            !isSorting &&
            checkAccess('features', 'productAndOption') &&
            handleOpenModal(option)
          }
          onRemove={() => {
            handleShowDeleteModal(option);
            setState(prev => ({ ...prev, synchronize: false }));
          }}
          onDuplicate={() => handleDuplicate(option)}
          body={
            <div className={classes.body}>
              <p className='truncText m-0' style={{ fontSize: UNIT * 0.75, color: '#646777' }}>
                {`Produit associé: ${attachedproduct?.name || '--'}`}
              </p>
              <p className='truncText m-0' style={{ fontSize: UNIT * 0.75, color: '#646777' }}>
                {`Sous liste d'options:${formatCategories(categoriesArray)}`}
              </p>
            </div>
          }
          hideSuppression={!canDelete}
        />
      </div>
    );
  }, [stableProps, state.isSorting, allProducts, handleOpenModal, handleShowDeleteModal, handleDuplicate, formatCategories]);

  return {
    state,
    setState,
    sortedOptions,
    allProducts,
    handleOpenModal,
    handleCloseModal,
    handleShowDeleteModal,
    handleDuplicate,
    handleRemove,
    handleDrag,
    handleStart,
    renderOptionItem
  };
};
